/**
 * ShopsMapPage — Google Maps view of user's shops.
 * 
 * Features:
 * - Shows all shops with location on Google Maps
 * - Different markers: green (public), red (private)
 * - Click marker → InfoWindow with shop details
 * - "Center on Me" floating button
 * - Shows count of shops without location
 * 
 * Uses direct <script> tag loading (no npm library) for maximum compatibility.
 */

import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useShopStore } from '../../stores/shopStore'
import { loadGoogleMaps } from '../../utils/loadGoogleMaps'
import './ShopsMapPage.css'

const GOOGLE_MAPS_API_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || ''

function toFiniteNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  if (typeof value === 'string') {
    const parsed = Number.parseFloat(value)
    if (Number.isFinite(parsed)) return parsed
  }
  return null
}

export default function ShopsMapPage() {
  const navigate = useNavigate()
  const { t } = useTranslation('shops')
  const { shops, fetchShops } = useShopStore()
  const mapRef = useRef<HTMLDivElement>(null)
  const [map, setMap] = useState<google.maps.Map | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const markersRef = useRef<google.maps.Marker[]>([])
  const mapInitializedRef = useRef(false)

  // Filter shops with location
  const shopsWithLocation = shops
    .map((shop) => {
      const lat = toFiniteNumber((shop as any).location?.lat)
      const lng = toFiniteNumber((shop as any).location?.lng)
      if (lat === null || lng === null) return null
      return { shop, position: { lat, lng } }
    })
    .filter((entry): entry is { shop: typeof shops[number]; position: google.maps.LatLngLiteral } => entry !== null)
  const shopsWithoutLocation = shops.length - shopsWithLocation.length

  useEffect(() => {
    fetchShops()
  }, [])

  // Initialize map once
  useEffect(() => {
    if (!mapRef.current || mapInitializedRef.current) return

    // Check if API key is available
    if (!GOOGLE_MAPS_API_KEY) {
      setError(t('errors.mapsApiKeyNotConfigured'))
      setLoading(false)
      return
    }

    let cancelled = false
    mapInitializedRef.current = true

    // Listen for Google Maps authentication errors
    const handleAuthError = () => {
      if (!cancelled) {
        setError(
          t('errors.mapsApiKeyRestricted')
        )
        setLoading(false)
      }
    }

    // Add global error listener
    ;(window as any).gm_authFailure = handleAuthError

    // Load Google Maps API using direct script tag injection
    const initMap = async () => {
      try {
        console.log('[ShopsMap] Loading Google Maps with key:', GOOGLE_MAPS_API_KEY.substring(0, 10) + '...')
        
        await loadGoogleMaps(GOOGLE_MAPS_API_KEY)

        console.log('[ShopsMap] Google Maps loaded successfully')

        // Check if component was unmounted during async operation
        if (cancelled || !mapRef.current) {
          return
        }

        // Default center (Dubai)
        const center: google.maps.LatLngLiteral = { lat: 25.0762, lng: 55.1329 }

        // Create map
        const mapInstance = new google.maps.Map(mapRef.current!, {
          center,
          zoom: 12,
          mapTypeControl: true,
          streetViewControl: false,
          fullscreenControl: false,
        })

        setMap(mapInstance)
        setLoading(false)
        console.log('[ShopsMap] Map created successfully')
      } catch (err) {
        console.error('[ShopsMap] Failed to load Google Maps:', err)
        if (!cancelled) {
          setError(t('errors.failedToLoadMaps'))
          setLoading(false)
        }
      }
    }

    initMap()

    // Cleanup on unmount
    return () => {
      cancelled = true
      mapInitializedRef.current = false
      delete (window as any).gm_authFailure
    }
  }, [shops]) // only re-run when shops data actually changes from store

  // Add/update markers when map is ready and shops change
  useEffect(() => {
    if (!map) return

    // Clear old markers
    markersRef.current.forEach((marker) => marker.setMap(null))
    markersRef.current = []

    if (shopsWithLocation.length === 0) {
      console.log('[ShopsMap] No shops with location to display')
      return
    }

    console.log('[ShopsMap] Adding', shopsWithLocation.length, 'markers')

    // Add markers for shops
    shopsWithLocation.forEach(({ shop, position }) => {
      const isPublic = shop.visibility === 'public'

      const marker = new google.maps.Marker({
        position,
        map,
        title: shop.name,
        icon: {
          path: google.maps.SymbolPath.CIRCLE,
          scale: 10,
          fillColor: isPublic ? '#10b981' : '#ef4444',
          fillOpacity: 1,
          strokeColor: '#fff',
          strokeWeight: 2,
        },
      })

      markersRef.current.push(marker)

      // InfoWindow content
      const infoContent = `
        <div style="padding: 8px; min-width: 200px;">
          <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #111;">
            ${shop.name}
            <span style="display: inline-block; margin-left: 8px; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: 600; ${isPublic ? 'background: #d1fae5; color: #065f46;' : 'background: #fee2e2; color: #991b1b;'}">
              ${isPublic ? t('public') : t('private')}
            </span>
          </h3>
          ${shop.address ? `<p style="margin: 4px 0; font-size: 13px; color: #666;">${shop.address}</p>` : ''}
          <p style="margin: 4px 0; font-size: 13px; color: #888;">
            ${t('sectionCount', { count: shop.sections.length })}
          </p>
          <div style="margin-top: 12px; display: flex; gap: 8px;">
            <button 
              onclick="window.openShopDetails('${shop.id}')" 
              style="padding: 6px 12px; background: #3b82f6; color: white; border: none; border-radius: 6px; font-size: 13px; font-weight: 600; cursor: pointer;"
            >
              ${t('details')}
            </button>
            <a 
              href="/?shopid=${shop.id}" 
              target="_blank"
              style="padding: 6px 12px; background: #059669; color: white; text-decoration: none; border-radius: 6px; font-size: 13px; font-weight: 600; display: inline-block;"
            >
              ${t('view3D')}
            </a>
          </div>
        </div>
      `

      const infoWindow = new google.maps.InfoWindow({
        content: infoContent,
      })

      marker.addListener('click', () => {
        infoWindow.open(map, marker)
      })
    })

    // Fit bounds to show all markers
    if (shopsWithLocation.length > 1) {
      const bounds = new google.maps.LatLngBounds()
      shopsWithLocation.forEach(({ position }) => {
        bounds.extend(position)
      })
      map.fitBounds(bounds, 50)
    } else if (shopsWithLocation.length === 1) {
      map.setCenter(shopsWithLocation[0].position)
      map.setZoom(15)
    }
  }, [map, shops]) // depend on map instance and shops from store (stable refs)

  // Global callback for InfoWindow "Details" button
  useEffect(() => {
    ;(window as any).openShopDetails = (_shopId: string) => {
      navigate('/shops')
    }
    return () => {
      delete (window as any).openShopDetails
    }
  }, [navigate])

  const handleCenterOnMe = () => {
    if (!map || !navigator.geolocation) return

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const pos = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
        }
        map.setCenter(pos)
        map.setZoom(15)
      },
      (err) => {
        alert(`Could not get your location: ${err.message}`)
      },
      { enableHighAccuracy: true, timeout: 10000 }
    )
  }

  return (
    <div className="shops-map-page">
      {/* Header */}
      <header className="shops-map-header">
        <button className="shops-map-back-btn" onClick={() => navigate('/shops')}>
          {t('common:back')}
        </button>
        <div style={{ width: 60 }} />
      </header>

      {/* Map Container */}
      {loading && (
        <div className="shops-map-loading">
          <div className="shops-map-spinner" />
          <p>{t('loadingMap')}</p>
        </div>
      )}

      {error && (
        <div className="shops-map-error">
          <p>⚠️ {error}</p>
          <button className="shops-map-btn" onClick={() => navigate('/shops')}>
            {t('backToShops')}
          </button>
        </div>
      )}

      {!error && <div ref={mapRef} className="shops-map-container" />}

      {/* Floating Actions */}
      {map && !error && (
        <>
          <button className="shops-map-center-btn" onClick={handleCenterOnMe} title={t('centerOnMyLocation')}>
            {t('centerOnMe')}
          </button>

          {/* Info Card */}
          <div className="shops-map-info-card">
            <p className="shops-map-info-stat">
              {t('shopsOnMap', { count: shopsWithLocation.length })}
            </p>
            {shopsWithoutLocation > 0 && (
              <p className="shops-map-info-warning">
                {t('shopsWithoutLocation', { count: shopsWithoutLocation })}
              </p>
            )}
          </div>
        </>
      )}
    </div>
  )
}
